/**
 * THE BACKFILL EXCEPTIONS REGISTER — web API (v24 Phase B, task B2.2).
 *
 * `GET /countries/:cc/payments/exceptions` — read-only. B3 writes the rows; the
 * API exposes no create, edit or clear, and this module mirrors that: one fetch,
 * no mutation. A row leaves the register by being RESOLVED, which the server
 * derives from the ledger (an `opening_balance` entry exists — PB-5), so there is
 * nothing here for a write to do.
 *
 * NOTE WHAT THE ROW TYPE DOES NOT CARRY: no amount, no pending, no received.
 * The register says WHICH CRFs need attention; the figures live on the CRF
 * record behind the ledger's own masking. The e2e holds this structurally, and
 * the page test scans the rendered rows for money-shaped text for the same
 * reason.
 */

import { apiRequest } from '../../lib/api';
import type { Paged } from '../registers/api';
export { registerErrorText } from '../registers/api';

/**
 * Migration 65's five (R18-A, 2026-09-02): the two archive anomalies and the three
 * withheld classes. `amount_mismatch` retired with that migration. The page never
 * re-labels these — `reason_label` is the server's — so the union exists only so a
 * stranger token is a type error in a test fixture, never a mystery on screen.
 */
export type BackfillExceptionReason =
  | 'negative_pending'
  | 'pending_gt_total'
  | 'amount_unexplained'
  | 'no_points'
  | 'zero_total';

export interface PaymentExceptionRow {
  id: string;
  crf_id: string;
  crf_doc_no: string;
  party_name: string;
  country_id: number;
  reason: BackfillExceptionReason;
  /** The reason IN WORDS — the server renders it; the page never re-labels an enum. */
  reason_label: string;
  archive_ref: string;
  created_at: string;
  /** Derived server-side: an `opening_balance` entry exists for the CRF (PB-5). */
  resolved: boolean;
}

export interface PaymentExceptionsFilters {
  includeResolved?: boolean;
  page?: number;
  perPage?: number;
}

export async function fetchPaymentExceptions(
  countryCode: string,
  filters: PaymentExceptionsFilters = {},
  signal?: AbortSignal,
): Promise<Paged<PaymentExceptionRow>> {
  return apiRequest<Paged<PaymentExceptionRow>>(`/countries/${countryCode}/payments/exceptions`, {
    query: {
      // The toggle is plainly-visible state: absent means "work still to do".
      include_resolved: filters.includeResolved === true ? '1' : undefined,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

export const paymentKeys = {
  exceptions: (cc: string, filters: PaymentExceptionsFilters) =>
    ['payments', cc, 'exceptions', filters] as const,
};
